import { buildProps, definePropType } from '@pit-element-plus/utils'
import type { FormRules } from '@pit-element-plus/components/form/src/types'
import type { ExtractPropTypes } from 'vue'
import type FormBase from './form-base.vue'

export const dialogTypes = ['dialog', 'dialog-header'] as const

export const InterfaceMethodTypes = ['GET', 'POST', 'PUT', 'DELETE'] as const

export const OperatingTypePropsTypes = ['add', 'edit', 'info'] as const

export interface SwithcConfigType {
  isShow?: any
  disabled?: boolean
  on?: (row: any) => void
  beforeChange?: (row: any) => void
}

export interface TagConfigType {
  isShow?: any
  on?: (row: any) => void
}

export interface FormBaseColumnsItem {
  prop: string
  label: string
  align?: string
  width?: string | number
  minWidth?: string | number
  sortable?: string | boolean
  render?: (row: any) => void
  showOverflow?: boolean
  fixed?: string
  hidden?: boolean
  children?: FormBaseColumnsItem[]
  isLink?: boolean
  isDetails?: boolean
  resizable?: boolean
  swithcConfig?: SwithcConfigType
  tagConfig?: TagConfigType
  dateFormatter?: any
  treeNode?: boolean
  renderTooltip?: any
  expand?: (row: any) => void
}

export type FormBaseColumns = FormBaseColumnsItem[]

export type FormBaseDialogBeforeCloseFn = () => void
export type FormBaseSearchBeforeCallbackFn = () => boolean
export type FormBaseSearchBeforeCallbackPreventFn = (_this: any) => Promise<any>
export type FormBaseAddBeforeCallbackFn = () => boolean
export type FormBaseAddBeforeAsyncCallbackFn = () => Promise<any>
export type FormBaseInfoBeforeCallbackFn = () => boolean
export type FormBaseAddParamControllerCallbackFn = () => Promise<any>
export type FormBaseAddParamPromiseControllerCallbackFn = (
  _this: any
) => Promise<any>
export type FormBaseAddParamControllerPreventCallbackFn = () => void
export type FormBaseInfoControllerCallbackFn = (row: any) => void
export type FormBaseAddControllerCallbackFn = (data: any) => void
export type FormBaseDelBeforeCallbackFn = (id: any) => Promise<boolean>
export type FormBaseDelControllerCallbackFn = () => void
export type FormBaseClearCallbackFn = () => void
export type FormBaseClearPreventCallbackFn = () => void

export type FormBaseDraftParamControllerCallback = () => Promise<any>
export type FormBaseDraftParamPromiseControllerCallback = (
  _this?: any
) => Promise<boolean>
export type FormBaseDraftParamControllerPreventCallback = (_this?: any) => void
export type FormBaseDraftControllerCallback = (row: any) => void
export type FormBaseDraftControllerErrorCallback = () => void
export type FormBaseProcessParamControllerCallback = () => Promise<any>
export type FormBaseProcessParamPromiseControllerCallback = (
  _this?: any
) => Promise<boolean>
export type FormBaseProcessParamControllerPreventCallback = () => void
export type FormBaseProcessControllerCallback = (row: any) => void
export type FormBaseProcessControllerErrorCallback = () => void
export type FormBaseProcessRunFlowParamsControllerCallback = (
  dataParams?: any,
  _this?: any
) => any

export const formBaseProps = buildProps({
  /**
   * @description 业务表主键
   */
  primaryKey: {
    type: String,
    default: 'id',
  },
  /**
   * @description 是否为树标识
   */
  rowKey: {
    type: String,
  },
  /**
   * @description 单选还是多选，默认多选
   */
  rowSingle: {
    type: Boolean,
    default: true,
  },
  /**
   * @description 表单显示类型，默认为弹窗
   */
  dialogType: {
    type: String,
    values: dialogTypes,
    default: 'dialog',
  },
  /**
   * @description 查询接口
   */
  queryInterface: String,
  /**
   * @description 单个查询接口
   */
  queryOneInterface: String,
  /**
   * @description 保存接口
   */
  saveInterface: String,
  /**
   * @description 修改接口
   */
  updateInterface: String,
  /**
   * @description 删除接口
   */
  deleteInterface: String,
  /**
   * @description 查询接口类型
   */
  queryInterfaceMethod: {
    type: String,
    values: InterfaceMethodTypes,
    default: 'GET',
  },
  /**
   * @description 单个查询接口类型
   */
  queryOneInterfaceMethod: {
    type: String,
    values: InterfaceMethodTypes,
    default: 'GET',
  },
  /**
   * @description 保存接口类型
   */
  saveInterfaceMethod: {
    type: String,
    values: InterfaceMethodTypes,
    default: 'POST',
  },
  /**
   * @description 修改接口类型
   */
  updateInterfaceMethod: {
    type: String,
    values: InterfaceMethodTypes,
    default: 'PUT',
  },
  /**
   * @description 删除接口类型
   */
  deleteInterfaceMethod: {
    type: String,
    values: InterfaceMethodTypes,
    default: 'DELETE',
  },
  /**
   * @description 搜索实体
   */
  formInline: {
    type: Object as () => Record<string, any> | any,
  },
  /**
   * @description 表头数据
   */
  columns: {
    type: definePropType<FormBaseColumns>(Array),
  },
  /**
   * @description 新增、修改实体
   */
  addForm: {
    type: Object as () => Record<string, any>,
  },
  /**
   * @description 新增、修改实体
   */
  rules: {
    type: definePropType<FormRules>(Object),
  },
  /**
   * @description 表单域标签的宽度
   */
  labelWidth: {
    type: String,
    default: '100px',
  },
  /**
   * @description 是否默认查询
   */
  isDefaultQuery: {
    type: Boolean,
    default: true,
  },
  /**
   * @description 用于判断是新建还是修改 新建add 修改edit
   */
  operatingTypeProps: {
    type: String,
    values: OperatingTypePropsTypes,
    default: 'add',
  },
  /**
   * @description 是否开启弹窗拖动
   */
  isDrag: {
    type: Boolean,
    default: true,
  },
  /**
   * @description 是否批量删除
   */
  isBatchDelete: {
    type: Boolean,
    default: false,
  },
  /**
   * @description 是否需要新增按钮
   */
  noAddBtn: {
    type: Boolean,
    default: true,
  },
  /**
   * @description 是否需要编辑按钮
   */
  noEditBtn: {
    type: Boolean,
    default: true,
  },
  /**
   * @description 是否需要删除按钮
   */
  noDelBtn: {
    type: Boolean,
    default: true,
  },
  /**
   * @description 是否需要查询按钮
   */
  noSearch: {
    type: Boolean,
    default: true,
  },
  /**
   * @description 是否需要清空按钮
   */
  noClear: {
    type: Boolean,
    default: true,
  },
  /**
   * @description 是否需要批量删除按钮
   */
  noBatchBtn: {
    type: Boolean,
    default: true,
  },
  /**
   * @description 是否隐藏弹窗确认按钮
   */
  noConfirmBtn: {
    type: Boolean,
    default: true,
  },
  /**
   * @description 是否隐藏弹窗取消按钮
   */
  noCancelBtn: {
    type: Boolean,
    default: true,
  },
  /**
   * @description 是否隐藏搜索区域
   */
  noSearchForm: {
    type: Boolean,
    default: true,
  },
  /**
   * @description 是否隐藏搜索区域左边表单部分
   */
  noSearchFormLeft: {
    type: Boolean,
    default: true,
  },
  /**
   * @description 是否隐藏搜索按钮
   */
  noFormInlineBtn: {
    type: Boolean,
    default: true,
  },
  /**
   * @description 是否功能按钮区域
   */
  noOperation: {
    type: Boolean,
    default: true,
  },
  /**
   * @description 是否隐藏折叠按钮
   */
  noExpandsBtn: {
    type: Boolean,
    default: true,
  },
  /**
   * @description 是否隐藏刷新按钮
   */
  noRefreshBtn: {
    type: Boolean,
    default: true,
  },
  /**
   * @description 是否隐藏序号
   */
  noTableIndex: {
    type: Boolean,
    default: true,
  },
  /**
   * @description 是否隐藏分页
   */
  noPagination: {
    type: Boolean,
    default: true,
  },
  /**
   * @description 自定义新增按钮的文本
   */
  addBtnText: {
    type: String,
    default: '',
  },
  /**
   * @description 自定义修改按钮的文本
   */
  editBtnText: {
    type: String,
    default: '',
  },
  /**
   * @description 自定义删除按钮的文本
   */
  delBtnText: {
    type: String,
    default: '',
  },
  /**
   * @description 自定义批量删除按钮的文本
   */
  batchBtnText: {
    type: String,
    default: '',
  },
  /**
   * @description 自定义插查询按钮的文本
   */
  searchBtnText: {
    type: String,
    default: '',
  },
  /**
   * @description 自定义清空按钮的文本
   */
  clearBtnText: {
    type: String,
    default: '',
  },
  /**
   * @description 自定义确定按钮的文本
   */
  confirmBtnText: {
    type: String,
    default: '',
  },
  /**
   * @description 弹窗的默认宽度
   */
  dialogWidth: {
    type: String,
    default: '30%',
  },
  /**
   * @description 弹窗关闭事件
   */
  dialogBeforeClose: {
    type: definePropType<FormBaseDialogBeforeCloseFn>(Function),
  },
  /**
   * @description 点击查询按钮前回调
   */
  searchBeforeCallback: {
    type: definePropType<FormBaseSearchBeforeCallbackFn>(Function),
  },
  /**
   * @description 新建请求前回调并阻止后续的事件
   */
  searchBeforeCallbackPrevent: {
    type: definePropType<FormBaseSearchBeforeCallbackPreventFn>(Function),
  },
  /**
   * @description 点击新建按钮前回调
   */
  addBeforeCallback: {
    type: definePropType<FormBaseAddBeforeCallbackFn>(Function),
  },
  /**
   * @description 点击新建按钮前回调
   */
  addBeforeAsyncCallback: {
    type: definePropType<FormBaseAddBeforeAsyncCallbackFn>(Function),
  },
  /**
   * @description 点击详情按钮前回调
   */
  infoBeforeCallback: {
    type: definePropType<FormBaseInfoBeforeCallbackFn>(Function),
  },
  /**
   * @description 新建请求前回调
   */
  addParamControllerCallback: {
    type: definePropType<FormBaseAddParamControllerCallbackFn>(Function),
  },
  /**
   * @description 新建请求前回调并，需return promise 并返回成功结果
   */
  addParamPromiseControllerCallback: {
    type: definePropType<FormBaseAddParamPromiseControllerCallbackFn>(Function),
  },
  /**
   * @description 新建请求前回调并阻止后续的事件
   */
  addParamControllerPreventCallback: {
    type: definePropType<FormBaseAddParamControllerPreventCallbackFn>(Function),
  },
  /**
   * @description 详情请求回调成功
   */
  infoControllerCallback: {
    type: definePropType<FormBaseInfoControllerCallbackFn>(Function),
  },
  /**
   * @description 新建/编辑 成功后的回调
   */
  addControllerCallback: {
    type: definePropType<FormBaseAddControllerCallbackFn>(Function),
  },
  /**
   * @description 删除 成功后的回调
   */
  delControllerCallback: {
    type: definePropType<FormBaseDelControllerCallbackFn>(Function),
  },
  /**
   * @description 删除前的回调
   */
  delBeforeCallback: {
    type: definePropType<FormBaseDelBeforeCallbackFn>(Function),
  },
  /**
   * @description 取消的回调
   */
  clearCallback: {
    type: definePropType<FormBaseClearCallbackFn>(Function),
  },
  /**
   * @description 自定义取消的回调
   */
  clearPreventCallback: {
    type: definePropType<FormBaseClearPreventCallbackFn>(Function),
  },
  /**
   * @description 您没有选中任何数据项,请选中后再操作！
   */
  textEmptyOption: {
    type: String,
    default: '',
  },
  /**
   * @description 请选择一条数据！
   */
  textEmptyOptionOne: {
    type: String,
    default: '',
  },
  /**
   * @description 新建成功！
   */
  textAddSuccess: {
    type: String,
    default: '',
  },
  /**
   * @description 修改成功！
   */
  textEditSuccess: {
    type: String,
    default: '',
  },
  /**
   * @description 删除成功！
   */
  textDelSuccess: {
    type: String,
    default: '',
  },
  /**
   * @description 是否删除已选中数据？
   */
  textDelStr: {
    type: String,
    default: '',
  },
  /**
   * @description 显示时间, 毫秒。设为 0 则不会自动关闭
   */
  duration: {
    type: Number,
    default: 3000,
  },
  /**
   * @description 重置后是否需要直接刷新
   */
  isClearQuery: {
    type: Boolean,
    default: false,
  },
  /**
   * @description 是否懒加载子节点数据
   */
  tableLazy: {
    type: Boolean,
    default: false,
  },
  /**
   * @description 是否懒加载子节点数据
   */
  isVirtuallyTable: {
    type: Boolean,
    default: false,
  },
  /**
   * @description 自动将列表转为树结构（支持虚拟滚动）
   */
  tableTransform: {
    type: Boolean,
    default: false,
  },
  /**
   * @description 懒加载匹配的key
   */
  lazyKey: {
    type: String,
    default: 'id',
  },
  /**
   * @description 自定义弹窗标题
   */
  customizeDialogTitle: {
    type: Object,
  },
  /**
   * @description 弹窗面板打开或关闭状态
   */
  isDialogVisible: {
    type: Boolean,
    default: false,
  },
  /**
   * @description 是否展开树形表格
   */
  defaultExpandAll: {
    type: Boolean,
    default: true,
  },
  /**
   * @description 多选 Checkbox 宽度
   */
  selectionWidth: {
    type: Number,
    default: 60,
  },
  /**
   * @description 多选 Checkbox 水平对齐
   */
  selectionAlign: {
    type: String,
    default: 'center',
  },
  /**
   * @description 多选 Checkbox 固定
   */
  selectionFixed: {
    type: [String, Boolean],
    default: false,
  },
  /**
   * @description 单选 Checkbox 宽度
   */
  indexWidth: {
    type: Number,
    default: 60,
  },
  /**
   * @description 单选 Checkbox 水平对齐
   */
  indexAlign: {
    type: String,
    default: 'center',
  },
  /**
   * @description 单选 Checkbox 固定
   */
  indexFixed: {
    type: [String, Boolean],
    default: false,
  },
  /**
   * @description form-base 弹窗高度
   */
  dialogTop: {
    type: String,
  },
  isPreviewMode: {
    type: Boolean,
    default: false,
  },
  /**
   * @description 是否双击查看详情
   */
  isDoubleClickDetails: {
    type: Boolean,
    default: true,
  },
  // 流程相关
  draftInterface: String,
  processInterface: String,
  draftInterfaceMethod: {
    type: String,
    default: 'POST',
  },
  processInterfaceMethod: {
    type: String,
    default: 'POST',
  },
  // 是否开启流程
  hasFlow: {
    type: Boolean,
    default: false,
  },
  // 流程对应的编码
  flowCode: {
    type: String,
    default: '',
  },
  // 是否隐藏暂存按钮
  noStorageBtn: {
    type: Boolean,
    default: false,
  },
  // 是否隐藏保存草稿按钮
  noDraftBtn: {
    type: Boolean,
    default: true,
  },
  // 是否隐藏发起流程按钮
  noInitiateProcessBtn: {
    type: Boolean,
    default: true,
  },
  // 自定义暂存按钮的文本
  storageBtnText: {
    type: String,
    default: '暂存',
  },
  // 自定义保存草稿按钮的文本
  draftBtnText: {
    type: String,
    default: '保存草稿',
  },
  // 自定义发起流程按钮的文本
  initiateProcessBtnText: {
    type: String,
    default: '发起流程',
  },
  // 保存草稿请求前回调
  draftParamControllerCallback: {
    type: definePropType<FormBaseDraftParamControllerCallback>(Function),
  },
  // 保存草稿请求前回调，需return promise 并返回成功结果
  draftParamPromiseControllerCallback: {
    type: definePropType<FormBaseDraftParamPromiseControllerCallback>(Function),
  },
  // 保存草稿请求前回调，没有需要处理返回值，不会往下执行
  draftParamControllerPreventCallback: {
    type: definePropType<FormBaseDraftParamControllerPreventCallback>(Function),
  },
  // 保存草稿请求成功回调
  draftControllerCallback: {
    type: definePropType<FormBaseDraftControllerCallback>(Function),
  },
  // 保存草稿请求失败回调
  draftControllerErrorCallback: {
    type: definePropType<FormBaseDraftControllerErrorCallback>(Function),
  },
  // 发起流程请求前回调
  processParamControllerCallback: {
    type: definePropType<FormBaseProcessParamControllerCallback>(Function),
  },
  // 发起流程请求前回调，需return promise 并返回成功结果
  processParamPromiseControllerCallback: {
    type: definePropType<FormBaseProcessParamPromiseControllerCallback>(
      Function
    ),
  },
  // 发起流程请求前回调，没有需要处理返回值，不会往下执行
  processParamControllerPreventCallback: {
    type: definePropType<FormBaseProcessParamControllerPreventCallback>(
      Function
    ),
  },
  // 发起流程请求成功回调
  processControllerCallback: {
    type: definePropType<FormBaseProcessControllerCallback>(Function),
  },
  // 发起流程请求失败回调
  processControllerErrorCallback: {
    type: definePropType<FormBaseProcessControllerErrorCallback>(Function),
  },
  // 流程发起前需要处理的参数
  processRunFlowParamsControllerCallback: {
    type: definePropType<FormBaseProcessRunFlowParamsControllerCallback>(
      Function
    ),
  },
  // 流程保存成功提示
  textDraftSuccess: {
    type: String,
    default: '',
  },
  // 流程发起成功提示
  textProcessSuccess: {
    type: String,
    default: '',
  },
  // 暂存按钮回调事件
  storageCallback: {
    type: Function,
    default: null,
  },
  // 弹窗取消按钮回调事件
  processCancelCallback: Function,
  // 暂存按钮请求前回调，没有需要处理返回值，不会往下执行
  storageControllerPreventCallback: {
    type: Function,
    default: null,
  },
  spanMethod: {
    type: Function,
  },
  flowStatusWidth: {
    type: Number,
  },
  // 流程状态版本（自定义处理）
  flowVersion: {
    type: String,
  },
  // 流程状态宽度
  tableFlowStatusWidth: {
    type: Number,
    default: 130,
  },
  // 是否开启设置按钮
  isSetUpRender: {
    type: Boolean,
    default: false,
  },
  rowClassName: {
    type: Function,
  },
  cellClassName: {
    type: Function,
  },
  validateOnRuleChange: {
    type: Boolean,
    default: true,
  },
} as const)
export type FormBaseProps = ExtractPropTypes<typeof formBaseProps>
export type FormBaseInstance = InstanceType<typeof FormBase>

export const formBaseEmits = {
  'result-table-back': (data: Array<any>, res?: any) => data && res,
  'table-current-change': (row: any) => row,
  'table-row-dblclick': (row: any) => row,
  'table-selection-change': (row: any) => row,
  'update:operatingTypeProps': (val: string) => val,
  'update:isDialogVisible': (val: boolean) => val,
}

export type FormBaseEmits = typeof formBaseEmits
